import { useEffect, useState } from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Snackbar,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import HumburgerButton from "../shared/HumburgerButton";
import useStore from "../shared/store";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";

// ─── Font injection ────────────────────────────────────────────────────────────
const injectFont = () => {
	if (document.getElementById("specialists-font")) return;
	const link = document.createElement("link");
	link.id = "specialists-font";
	link.rel = "stylesheet";
	link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap";
	document.head.appendChild(link);
};

// ─── Inline styles ─────────────────────────────────────────────────────────────
const injectStyles = () => {
	if (document.getElementById("specialists-styles")) return;
	const style = document.createElement("style");
	style.id = "specialists-styles";
	style.textContent = `
		.sp-wrap, .sp-wrap *, .sp-wrap *::before, .sp-wrap *::after {
			box-sizing: border-box;
		}
		.sp-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			padding: 32px 40px;
			background: #F5F3F0;
			min-height: 100vh;
		}

		/* ── Page header ── */
		.sp-head {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
			margin-bottom: 20px;
			flex-wrap: wrap;
		}
		.sp-title-row {
			display: flex;
			align-items: center;
			gap: 14px;
			flex: 1;
			min-width: 200px;
		}
		.sp-title {
			font-family: 'Manrope', sans-serif;
			font-size: 28px;
			font-weight: 800;
			color: #18181A;
			letter-spacing: -0.02em;
			white-space: nowrap;
		}
		.sp-title__line {
			flex: 1;
			height: 2px;
			background: linear-gradient(90deg, rgba(228,112,41,0.45) 0%, transparent 100%);
			border-radius: 2px;
		}

		.sp-btn-create {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 11px 22px;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			border: none;
			font-size: 13.5px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			white-space: nowrap;
			box-shadow: 0 4px 14px -4px rgba(228,112,41,0.45);
			transition: background 0.15s, transform 0.1s;
		}
		.sp-btn-create:hover { background: #B94D1A; }
		.sp-btn-create:active { transform: scale(0.98); }

		/* ── Search bar ── */
		.sp-search {
			display: flex;
			align-items: center;
			background: #fff;
			border: 1.5px solid rgba(0,0,0,0.1);
			border-radius: 12px;
			padding: 10px 16px;
			margin-bottom: 20px;
			gap: 10px;
			transition: border-color 0.15s, box-shadow 0.15s;
		}
		.sp-search:focus-within {
			border-color: #E47029;
			box-shadow: 0 0 0 3px rgba(228,112,41,0.12);
		}
		.sp-search input {
			flex: 1;
			border: none;
			outline: none;
			font-size: 14px;
			font-family: inherit;
			background: transparent;
			color: #18181A;
			min-width: 0;
		}
		.sp-search input::placeholder { color: #9A9A9F; }

		/* ── Empty state ── */
		.sp-empty {
			background: #FEF0E7;
			border: 1.5px dashed #E47029;
			border-radius: 14px;
			padding: 32px 20px;
			text-align: center;
			color: #B94D1A;
			font-weight: 600;
			font-size: 13.5px;
		}

		/* ── Table card ── */
		.sp-table-card {
			background: #fff;
			border: 1px solid rgba(0,0,0,0.07);
			border-radius: 16px;
			overflow: hidden;
			box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 8px 24px -8px rgba(0,0,0,0.08);
		}
		.sp-table-scroll { overflow-x: auto; }
		.sp-table { width: 100%; border-collapse: collapse; min-width: 480px; }
		.sp-table thead th {
			background: #FEF0E7;
			color: #B94D1A;
			font-weight: 700;
			font-size: 12px;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			text-align: left;
			padding: 13px 18px;
			border-bottom: 2px solid #F5D5C0;
			white-space: nowrap;
		}
		.sp-table tbody td {
			padding: 13px 18px;
			font-size: 13.5px;
			color: #18181A;
			border-bottom: 1px solid rgba(0,0,0,0.05);
			vertical-align: middle;
		}
		.sp-table tbody tr:last-child td { border-bottom: none; }
		.sp-table tbody tr:hover { background: #FAFAFA; }

		.sp-email-cell {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 8px;
		}
		.sp-email-link {
			color: #E47029;
			text-decoration: none;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			min-width: 0;
			font-weight: 500;
		}
		.sp-email-link:hover { text-decoration: underline; }
		.sp-subtle { color: #9A9A9F; }

		.sp-delete-btn {
			width: 30px; height: 30px;
			border-radius: 8px;
			border: none;
			background: transparent;
			color: #C0BDB8;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			flex-shrink: 0;
			transition: background 0.15s, color 0.15s;
		}
		.sp-delete-btn:hover { background: #FCEBEB; color: #E53E3E; }

		/* ── Paginator row ── */
		.sp-paginator-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 16px;
			margin-top: 16px;
			flex-wrap: wrap;
		}
		.sp-rpp {
			display: flex;
			align-items: center;
			gap: 8px;
		}
		.sp-rpp label {
			font-size: 12px;
			color: #6B6760;
			font-weight: 600;
			white-space: nowrap;
		}
		.sp-rpp select {
			padding: 8px 12px;
			border: 1.5px solid rgba(0,0,0,0.1);
			border-radius: 9px;
			font-size: 13px;
			font-family: inherit;
			background: #fff;
			color: #18181A;
			cursor: pointer;
		}
		.sp-rpp select:focus { outline: none; border-color: #E47029; }

		.sp-pagination .MuiPaginationItem-root.Mui-selected {
			background-color: #E47029 !important;
			color: #fff !important;
		}
		.sp-pagination .MuiPaginationItem-root:hover {
			background-color: #FEF0E7 !important;
		}

		/* ── Dialog content (functional MUI Dialog kept) ── */
		.sp-dialog-title {
			font-family: 'Manrope', sans-serif;
			font-size: 18px;
			font-weight: 800;
			color: #18181A;
			margin-bottom: 4px;
		}
		.sp-dialog-desc {
			font-size: 13px;
			color: #6B6760;
			line-height: 1.55;
			margin: 0;
		}
		.sp-field { margin-top: 16px; }
		.sp-field label {
			display: block;
			font-size: 11.5px;
			font-weight: 600;
			color: #5B5B60;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			margin-bottom: 7px;
		}
		.sp-field-control {
			display: flex;
			align-items: center;
			gap: 10px;
			border: 1.5px solid rgba(0,0,0,0.11);
			border-radius: 10px;
			padding: 0 14px;
			background: #FAFAFA;
			transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
		}
		.sp-field-control:focus-within {
			border-color: #E47029;
			box-shadow: 0 0 0 3px rgba(228,112,41,0.12);
			background: #fff;
		}
		.sp-field-control.error { border-color: #E53E3E; }
		.sp-field-control svg { flex-shrink: 0; color: #E47029; }
		.sp-field-control input {
			flex: 1;
			border: none;
			outline: none;
			height: 46px;
			font-size: 14px;
			font-family: inherit;
			background: transparent;
			color: #18181A;
			min-width: 0;
		}
		.sp-field-error { font-size: 11px; color: #E53E3E; margin-top: 4px; }

		.sp-btn-ghost {
			padding: 9px 18px;
			border-radius: 10px;
			background: #FEF0E7;
			color: #E47029;
			border: none;
			font-size: 13px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.sp-btn-ghost:hover:not(:disabled) { background: #FBCFAC; }
		.sp-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

		.sp-btn-primary {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 9px 20px;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			border: none;
			font-size: 13px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.sp-btn-primary:hover:not(:disabled) { background: #B94D1A; }
		.sp-btn-primary:disabled { background: #E5C9B0; cursor: not-allowed; }

		.sp-btn-danger {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 9px 20px;
			border-radius: 10px;
			background: #E53E3E;
			color: #fff;
			border: none;
			font-size: 13px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.sp-btn-danger:hover:not(:disabled) { background: #C53030; }
		.sp-btn-danger:disabled { background: #F0B4B4; cursor: not-allowed; }

		.sp-confirm-row {
			display: flex;
			gap: 6px;
			font-size: 13.5px;
			color: #18181A;
			margin-bottom: 6px;
		}
		.sp-confirm-row:last-child { margin-bottom: 0; }
		.sp-confirm-row strong { color: #6B6760; font-weight: 600; min-width: 56px; }

		/* ── Responsive ── */
		@media (max-width: 768px) {
			.sp-wrap { padding: 24px 20px; }
		}
		@media (max-width: 480px) {
			.sp-head { flex-direction: column; align-items: stretch; }
			.sp-btn-create { justify-content: center; }
			.sp-paginator-row { flex-direction: column; align-items: stretch; }
			.sp-rpp { justify-content: space-between; }
		}
	`;
	document.head.appendChild(style);
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const HospitalIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17" aria-hidden="true">
		<path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3" />
		<path d="M3 9h18l-1 11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
		<line x1="12" y1="13" x2="12" y2="17" />
		<line x1="10" y1="15" x2="14" y2="15" />
	</svg>
);
const PersonIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17" aria-hidden="true">
		<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
		<circle cx="12" cy="7" r="4" />
	</svg>
);
const EmailIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17" aria-hidden="true">
		<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
		<polyline points="22,6 12,13 2,6" />
	</svg>
);
const SearchIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#9A9A9F" strokeWidth="2" width="18" height="18" aria-hidden="true">
		<circle cx="11" cy="11" r="8" />
		<line x1="21" y1="21" x2="16.65" y2="16.65" />
	</svg>
);
const PlusIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15" aria-hidden="true">
		<line x1="12" y1="5" x2="12" y2="19" />
		<line x1="5" y1="12" x2="19" y2="12" />
	</svg>
);
const DeleteIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
		<polyline points="3 6 5 6 21 6" />
		<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
		<path d="M10 11v6M14 11v6" />
	</svg>
);

// ─── Main component ────────────────────────────────────────────────────────────
const SpecialistsPage = ({ smallScreenView, handleMenuClick }) => {
	const { user } = useStore();
	const [orgSpecialists, setOrgSpecialists] = useState([]);

	const [open, setOpen] = useState(false);
	const [formData, setFormData] = useState({ email: "", clinicName: "", doctor: "" });
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [toDelete, setToDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const [toast, setToast] = useState({ open: false, msg: "" });

	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const handleFormData = (name, value) => {
		setFormData((data) => ({ ...data, [name]: value }));
	};

	const fetchOrgSpecialists = async () => {
		const orgId = user.orgs[0];
		const specialists = await api(
			apiConfigs.baseOption("get", `specialists/${orgId}/all`)
		);
		const data = specialists.data;
		setOrgSpecialists(data);
		return data;
	};

	useEffect(() => {
		if (user) fetchOrgSpecialists();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const rows = Array.isArray(orgSpecialists) ? orgSpecialists : [];

	const filteredRows = rows.filter(
		(row) =>
			row.clinic?.toLowerCase().includes(search.toLowerCase()) ||
			row.clinicName?.toLowerCase().includes(search.toLowerCase())
	);

	const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

	const pagedRows =
		rowsPerPage > 0
			? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			: filteredRows;

	useEffect(() => {
		setPage(0);
	}, [search, rowsPerPage]);

	const handleChangePageNumbered = (event, newPageOneBased) => {
		setPage(newPageOneBased - 1);
	};

	const handleChangeRowsPerPage = (event) => {
		const next = parseInt(event.target.value, 10);
		setRowsPerPage(next);
	};

	const validate = () => {
		const next = {};
		if (!formData.clinicName?.trim()) next.clinicName = "Clinic name is required";
		if (!formData.doctor?.trim()) next.doctor = "Doctor name is required";
		if (!formData.email?.trim()) next.email = "Email is required";
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
			next.email = "Enter a valid email";
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleCreate = async (e) => {
		e.preventDefault();
		if (!validate()) return;

		setLoading(true);
		const orgId = user.orgs[0];
		try {
			await api(
				apiConfigs.baseOption("post", `specialists/create`, {
					data: {
						orgId,
						name: formData.doctor,
						clinicName: formData.clinicName,
						email: formData.email,
					},
				})
			);

			const updated = await fetchOrgSpecialists();
			const total = updated.length;
			const lastPage = Math.max(0, Math.floor((total - 1) / rowsPerPage));
			setPage(lastPage);

			setOpen(false);
			setFormData({});
			setErrors({});
		} finally {
			setLoading(false);
		}
	};

	const requestDelete = (row) => {
		setToDelete(row);
		setConfirmOpen(true);
	};

	const confirmDelete = async () => {
		if (!toDelete) return;
		setDeleting(true);

		try {
			setOrgSpecialists((prev) => prev.filter((r) => r !== toDelete));

			await api(apiConfigs.baseOption("delete", `specialists/${toDelete._id}`));
			await fetchOrgSpecialists();

			setToast({ open: true, msg: "Specialist removed from the list" });
		} finally {
			setDeleting(false);
			setConfirmOpen(false);
			setToDelete(null);
		}
	};

	return (
		<>
			<HumburgerButton smallScreenView={smallScreenView} handleMenuClick={handleMenuClick} />

			<div className="sp-wrap">

				{/* ── Page header ── */}
				<div className="sp-head">
					<div className="sp-title-row">
						<span className="sp-title">Specialists</span>
						<div className="sp-title__line" />
					</div>
					<button className="sp-btn-create" onClick={() => setOpen(true)}>
						<PlusIcon />
						Create Specialist
					</button>
				</div>

				{/* ── Search ── */}
				<div className="sp-search">
					<SearchIcon />
					<input
						placeholder="Search by clinic name"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>

				{filteredRows.length === 0 ? (
					<div className="sp-empty">
						No clinics found. Try a different search or create a new one.
					</div>
				) : (
					<>
						<div className="sp-table-card">
							<div className="sp-table-scroll">
								<table className="sp-table" aria-label="Specialists list table">
									<thead>
										<tr>
											<th>Clinic</th>
											<th>Doctor</th>
											<th>Email</th>
										</tr>
									</thead>
									<tbody>
										{pagedRows.map((row, idx) => (
											<tr key={idx}>
												<td>{row.clinic || row.clinicName || "—"}</td>
												<td>{row.name || "—"}</td>
												<td>
													<div className="sp-email-cell">
														{row.email ? (
															<a href={`mailto:${row.email}`} className="sp-email-link">
																{row.email}
															</a>
														) : (
															<span className="sp-subtle">—</span>
														)}
														<button
															aria-label="Delete"
															className="sp-delete-btn"
															onClick={() => requestDelete(row)}
														>
															<DeleteIcon />
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>

						{/* ── Pagination row ── */}
						<div className="sp-paginator-row">
							<div className="sp-rpp">
								<label htmlFor="rpp-select">Rows per page</label>
								<select
									id="rpp-select"
									value={rowsPerPage}
									onChange={handleChangeRowsPerPage}
								>
									<option value={10}>10</option>
									<option value={15}>15</option>
								</select>
							</div>

							<Pagination
								className="sp-pagination"
								count={totalPages}
								page={page + 1}
								onChange={handleChangePageNumbered}
								showFirstButton
								showLastButton
								shape="rounded"
							/>
						</div>
					</>
				)}
			</div>

			{/* ── Create dialog ── */}
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				aria-labelledby="sl-dialog-title"
				fullWidth
				maxWidth="sm"
				PaperProps={{ style: { borderRadius: 18 } }}
			>
				<DialogTitle id="sl-dialog-title" disableTypography>
					<div className="sp-dialog-title">Referral, Specialists Form</div>
					<p className="sp-dialog-desc">
						Use this form to create a referral entry in your Specialists. Include the clinic name, the doctor name, and the email address.
					</p>
				</DialogTitle>

				<DialogContent dividers>
					<form onSubmit={handleCreate} id="sl-form">
						<div className="sp-field">
							<label htmlFor="clinicName">Clinic name</label>
							<div className={`sp-field-control${errors.clinicName ? " error" : ""}`}>
								<HospitalIcon />
								<input
									id="clinicName"
									value={formData.clinicName || ""}
									onChange={(e) => handleFormData("clinicName", e.target.value)}
								/>
							</div>
							{errors.clinicName && <div className="sp-field-error">{errors.clinicName}</div>}
						</div>

						<div className="sp-field">
							<label htmlFor="doctor">Doctor name</label>
							<div className={`sp-field-control${errors.doctor ? " error" : ""}`}>
								<PersonIcon />
								<input
									id="doctor"
									value={formData.doctor || ""}
									onChange={(e) => handleFormData("doctor", e.target.value)}
								/>
							</div>
							{errors.doctor && <div className="sp-field-error">{errors.doctor}</div>}
						</div>

						<div className="sp-field">
							<label htmlFor="email">Email</label>
							<div className={`sp-field-control${errors.email ? " error" : ""}`}>
								<EmailIcon />
								<input
									id="email"
									type="email"
									value={formData.email || ""}
									onChange={(e) => handleFormData("email", e.target.value)}
								/>
							</div>
							{errors.email && <div className="sp-field-error">{errors.email}</div>}
						</div>
					</form>
				</DialogContent>

				<DialogActions>
					<button className="sp-btn-ghost" onClick={() => setOpen(false)}>
						Cancel
					</button>
					<button
						form="sl-form"
						type="submit"
						className="sp-btn-primary"
						disabled={loading}
					>
						{loading && <CircularProgress size={16} color="inherit" />}
						{loading ? "Creating..." : "Create New Clinic"}
					</button>
				</DialogActions>
			</Dialog>

			{/* ── Delete confirmation dialog ── */}
			<Dialog
				open={confirmOpen}
				onClose={() => (!deleting ? setConfirmOpen(false) : null)}
				aria-labelledby="confirm-delete-title"
				fullWidth
				maxWidth="xs"
				PaperProps={{ style: { borderRadius: 18 } }}
			>
				<DialogTitle id="confirm-delete-title" disableTypography>
					<div className="sp-dialog-title">Delete specialist</div>
					<p className="sp-dialog-desc">
						You are about to remove this specialist from the list. This change is UI only.
					</p>
				</DialogTitle>

				<DialogContent dividers>
					<div className="sp-confirm-row">
						<strong>Clinic:</strong> {toDelete?.clinic || toDelete?.clinicName || "—"}
					</div>
					<div className="sp-confirm-row">
						<strong>Doctor:</strong> {toDelete?.name || "—"}
					</div>
					<div className="sp-confirm-row">
						<strong>Email:</strong> {toDelete?.email || "—"}
					</div>
				</DialogContent>

				<DialogActions>
					<button
						className="sp-btn-ghost"
						onClick={() => setConfirmOpen(false)}
						disabled={deleting}
					>
						Cancel
					</button>
					<button
						className="sp-btn-danger"
						onClick={confirmDelete}
						disabled={deleting}
					>
						{deleting && <CircularProgress size={16} color="inherit" />}
						{deleting ? "Deleting..." : "Delete"}
					</button>
				</DialogActions>
			</Dialog>

			{/* ── Toast ── */}
			<Snackbar
				open={toast.open}
				onClose={() => setToast({ open: false, msg: "" })}
				message={toast.msg}
				autoHideDuration={2200}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			/>
		</>
	);
};

export default SpecialistsPage;